import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Users, DollarSign, Target, CheckCircle2, Zap, BarChart3, MessageSquare, Mail } from 'lucide-react';
import { Button } from '../components/ui/Button';
// Mock database for the 4 specific case studies
const CASE_STUDY_DATA: Record<string, any> = {
  omnicredit: {
    client: 'Omnicredit',
    industry: 'Financial Services',
    timeline: '4 Months',
    title: 'From Zero to $40K/Month in 4 Months',
    stats: [{
      number: '$40K',
      label: 'Monthly Revenue',
      icon: DollarSign
    }, {
      number: '0 to 100+',
      label: 'Daily Leads',
      icon: Users
    }, {
      number: '4x',
      label: 'ROAS',
      icon: TrendingUp
    }]
  },
  'christmas-palace': {
    client: 'The Christmas Palace',
    industry: 'E-commerce',
    timeline: '12 Months',
    title: 'Turning Seasonality Into a Scalable Revenue Engine',
    stats: [{
      number: '+56%',
      label: 'Net Sales Growth',
      icon: TrendingUp
    }, {
      number: '+102%',
      label: 'Email Revenue Growth',
      icon: BarChart3
    }, {
      number: '+1,919%',
      label: 'SMS Growth',
      icon: Zap
    }],
    challenge: 'A premium brand with strong demand—but a fragile growth model. The Christmas Palace relied heavily on discounts and peak-season spikes to drive revenue, leaving little control over performance the rest of the year. Limited creative output and underdeveloped channels like SMS and B2B restricted scalability, turning growth into a reactive process instead of a predictable system.',
    approach: 'We engineered a performance-driven ecosystem designed for scale. By massively increasing creative production, restructuring paid media, and activating email, SMS, and B2B as core revenue drivers, we created a system that could continuously generate, capture, and convert demand. Every layer—from traffic to conversion—was optimized to work together, not in isolation.',
    result: 'The business evolved from seasonal dependency into sustained, year-round growth. Revenue scaled, margins improved, and reliance on discounts dropped to historic lows—all while maintaining strong performance across channels. What was once reactive became predictable: a brand now driven by systems, not seasons.',
    testimonial: {
      quote: 'Engage Media completely changed how we think about growth. We went from chasing seasonal spikes to running a real, scalable business. The results speak for themselves.',
      name: 'The Christmas Palace Team',
      title: 'Leadership, The Christmas Palace'
    }
  },
  'levelup-insurance': {
    client: 'Levelup Insurance',
    industry: 'Insurance',
    timeline: '6 Months',
    title: 'From $3K to $70K/Month: Scaling High-Quality Leads Nationwide',
    stats: [{
      number: '$70K',
      label: 'Monthly Ad Spend',
      icon: DollarSign
    }, {
      number: '-45%',
      label: 'Cost Per Lead',
      icon: TrendingUp
    }, {
      number: '10x',
      label: 'Volume Growth',
      icon: BarChart3
    }]
  },
  'z-roofing': {
    client: 'Z Roofing and Waterproofing',
    industry: 'Local Services',
    timeline: '12 Months',
    title: 'From Invisible to Trusted: Building a Local Authority Brand',
    stats: [{
      number: '200%',
      label: 'Lead Increase',
      icon: TrendingUp
    }, {
      number: '#1',
      label: 'Local Market Share',
      icon: Target
    }, {
      number: '5x',
      label: 'ROI on Ad Spend',
      icon: DollarSign
    }]
  },
  'the-christmas-palace-2': {
    client: 'Casa Wagyu',
    industry: 'Food & Beverage',
    timeline: '8 Months',
    title: 'Filling Tables and Building a Premium Brand Online',
    stats: [{
      number: '+56%',
      label: 'Revenue Growth',
      icon: TrendingUp
    }, {
      number: '+102%',
      label: 'Email Revenue Growth',
      icon: Mail
    }, {
      number: '+1,919%',
      label: 'SMS Growth',
      icon: MessageSquare
    }]
  },
  'the-christmas-palace-3': {
    client: 'Xclusive Agent Leads',
    industry: 'Real Estate',
    timeline: '6 Months',
    title: 'Dominating Agent Lead Gen With a Full-Funnel System',
    stats: [{
      number: '$120K',
      label: 'Monthly Revenue',
      icon: DollarSign
    }, {
      number: '6.2x',
      label: 'ROAS',
      icon: TrendingUp
    }, {
      number: '-38%',
      label: 'Cost Per Lead',
      icon: BarChart3
    }]
  },
  'winners-mindset': {
    client: 'Winners Mindset',
    industry: 'Coaching & Education',
    timeline: '10 Months',
    title: 'Scaling a Coaching Brand From Side Hustle to 7 Figures',
    stats: [{
      number: '7-Figure',
      label: 'Annual Revenue',
      icon: DollarSign
    }, {
      number: '4.8x',
      label: 'ROAS',
      icon: TrendingUp
    }, {
      number: '15K+',
      label: 'Students Enrolled',
      icon: Users
    }]
  }
};
export function CaseStudyDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!slug || !CASE_STUDY_DATA[slug]) {
    return <Navigate to="/case-studies" replace data-id="element-666" />;
  }
  const data = CASE_STUDY_DATA[slug];
  return <div className="w-full pt-24 pb-24 bg-engage-black min-h-screen" data-id="element-667">
      <div className="max-w-6xl mx-auto px-4" data-id="element-668">
        {/* Back Link */}
        <Link to="/case-studies" className="inline-flex items-center text-engage-gray4 hover:text-engage-yellow transition-colors mb-12 font-medium" data-id="element-669">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-670" />
          Back to Case Studies
        </Link>

        {/* Hero */}
        <div className="mb-16 max-w-4xl" data-id="element-671">
          <div className="flex flex-wrap gap-3 mb-6" data-id="element-672">
            <span className="px-4 py-1.5 bg-engage-yellow/10 text-engage-yellow border border-engage-yellow/20 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-673">
              {'E-Commerce'}
            </span>
            <span className="px-4 py-1.5 bg-engage-gray2 text-engage-gray5 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-674">
              Email + SMS
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-engage-white tracking-tight mb-4" data-id="element-675">
            {'Omnicredit'}
          </h1>
          <h2 className="text-2xl md:text-3xl text-engage-gray4 font-medium leading-tight" data-id="element-676">
            {'From Zero to $40K/Month in 4 Months'}
          </h2>
        </div>

        {/* Key Results Stats - Redesigned */}
        <div className="mb-16" data-id="element-677">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-678">
            {data.stats.map((stat: any, i: number) => {
            const Icon = stat.icon || Zap;
            return <div key={i} className="bg-engage-gray1 border border-engage-gray2 rounded-2xl p-8 relative overflow-hidden group hover:border-engage-yellow/50 transition-colors" data-id="element-679">
                  <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity" data-id="element-680">
                    <Icon className="w-24 h-24 text-engage-yellow" data-id="element-681" />
                  </div>
                  <div className="relative z-10 flex flex-col h-full justify-between" data-id="element-682">
                    <div className="w-12 h-12 bg-engage-black border border-engage-gray2 rounded-xl flex items-center justify-center mb-6" data-id="element-683">
                      <Icon className="w-6 h-6 text-engage-yellow" data-id="element-684" />
                    </div>
                    <div data-id="element-685">
                      <div className="text-4xl md:text-5xl font-black text-engage-white mb-2 tracking-tight" data-id="element-686">
                        {stat.number}
                      </div>
                      <div className="text-engage-gray4 font-medium uppercase tracking-wider text-sm" data-id="element-687">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-10" data-id="element-688">
          {/* The Challenge */}
          <div className="bg-engage-gray1 border border-engage-gray2 rounded-2xl p-8 md:p-12" data-id="element-689">
            <h3 className="text-2xl md:text-3xl font-bold text-engage-white mb-3" data-id="element-690">
              The Challenge
            </h3>
            <div className="w-16 h-1 bg-engage-yellow rounded-full mb-6" data-id="element-691" />
            <p className="text-engage-gray4 text-base md:text-lg leading-relaxed" data-id="element-692">
              {'OmniCredit entered the market with no existing growth infrastructure\u2014no revenue history, no validated digital acquisition system, and no funnel in place to consistently generate or convert demand. Like many early-stage financial brands, the challenge wasn\u2019t just attracting attention, but doing so with enough clarity, trust, and precision to bring qualified leads into the business. The goal was not simply to launch fast, but to build the right system from day one.'}
            </p>
          </div>

          {/* Our Approach & The Result - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-693">
            <div className="bg-engage-gray1 border border-engage-gray2 rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-694">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[radial-gradient(circle_at_top_right,rgba(252,198,13,0.12)_0%,transparent_70%)]" data-id="element-695" />
              <div className="relative z-10" data-id="element-696">
                <h3 className="text-2xl md:text-3xl font-bold text-engage-white mb-3" data-id="element-697">
                  Our Approach
                </h3>
                <div className="w-16 h-1 bg-engage-yellow rounded-full mb-6" data-id="element-698" />
                <p className="text-engage-gray4 text-base md:text-lg leading-relaxed" data-id="element-699">
                  {data.approach || 'We built a complete acquisition ecosystem from the ground up. That included conversion-focused landing pages, a clear funnel architecture, stronger messaging and positioning, performance creatives designed for testing, and a paid media engine structured for scale. Every part of the system\u2014from traffic to conversion\u2014was built to work together, while ongoing optimization allowed us to refine what worked and accelerate growth quickly.'}
                </p>
              </div>
            </div>
            <div className="bg-engage-gray1 border border-engage-gray2 rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-700">
              <div className="absolute top-0 right-0 w-40 h-40 bg-[radial-gradient(circle_at_top_right,rgba(252,198,13,0.12)_0%,transparent_70%)]" data-id="element-701" />
              <div className="relative z-10" data-id="element-702">
                <h3 className="text-2xl md:text-3xl font-bold text-engage-white mb-3" data-id="element-703">
                  The Result
                </h3>
                <div className="w-16 h-1 bg-engage-yellow rounded-full mb-6" data-id="element-704" />
                <p className="text-engage-gray4 text-base md:text-lg leading-relaxed" data-id="element-705">
                  {data.result || 'The business evolved from seasonal dependency into sustained, year-round growth. Revenue scaled, margins improved, and reliance on discounts dropped to historic lows\u2014all while maintaining strong performance across channels. What was once reactive became predictable: a brand now driven by systems, not seasons.'}
                </p>
              </div>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-engage-gray1 border border-engage-gray2 p-10 md:p-16 lg:p-20 rounded-2xl relative overflow-hidden text-center" data-id="element-706">
            <div className="flex justify-center mb-8" data-id="element-707">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className="text-engage-yellow" data-id="element-708">
                <path d="M14.017 21L16.411 14.182C16.411 14.182 15.045 14.182 15.045 10.773C15.045 7.36364 18.006 5 21 5C23.994 5 24 7.36364 24 7.36364C24 7.36364 22.634 7.36364 22.634 10.773C22.634 14.182 19.909 21 19.909 21H14.017ZM3.017 21L5.411 14.182C5.411 14.182 4.045 14.182 4.045 10.773C4.045 7.36364 7.006 5 10 5C12.994 5 13 7.36364 13 7.36364C13 7.36364 11.634 7.36364 11.634 10.773C11.634 14.182 8.909 21 8.909 21H3.017Z" data-id="element-709" />
              </svg>
            </div>
            <blockquote className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium italic text-engage-white mb-10 md:mb-12 leading-relaxed relative z-10 max-w-4xl mx-auto" data-id="element-710">
              {data.testimonial?.quote || "We didn't just scale revenue\u2014we built a system that generates it, with less reliance on discounts and more control over growth."}
            </blockquote>
            <div className="flex flex-col items-center relative z-10" data-id="element-711">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full mb-4 border-2 border-engage-gray2 flex items-center justify-center overflow-hidden bg-gradient-to-br from-engage-gray2 to-engage-gray3" data-id="element-712">
                <svg className="w-9 h-9 md:w-11 md:h-11 text-engage-gray4" viewBox="0 0 24 24" fill="currentColor" data-id="element-713">
                  <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" data-id="element-714" />
                </svg>
              </div>
              <div className="font-bold text-engage-white text-lg" data-id="element-715">
                {data.testimonial?.name || 'John Doe'}
              </div>
              <div className="text-engage-yellow font-medium text-sm uppercase tracking-wider" data-id="element-716">
                {data.testimonial?.title || 'CEO, The Christmas Palace'}
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 text-center bg-engage-gray1 border border-engage-gray2 rounded-2xl p-12 md:p-20 relative overflow-hidden" data-id="element-717">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(252,198,13,0.08)_0%,transparent_100%)]" data-id="element-718" />
          <div className="relative z-10 max-w-4xl mx-auto" data-id="element-719">
            <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight" data-id="element-720">
              Ready to Build Your Growth Engine?
            </h2>
            <p className="text-engage-gray4 text-xl mb-10" data-id="element-721">
              Stop guessing with your marketing budget. Let's build a
              predictable system for your brand.
            </p>
            <Button to="/contact" variant="primary" className="text-lg px-12 py-5" data-id="element-722">
              Book a Call With Us
            </Button>
          </div>
        </div>
      </div>
    </div>;
}